import { Box, Typography } from '@mui/material';

import dayjs from '../../../../config/dayjs';
import { BlockTitle, FieldGrid, ObjectTable } from './ReportViewer';
import { HardwareComparison } from './HardwareComparison';

const DATE_FORMAT = 'DD MMM YYYY, hh:mm A';
const MUTED = '#98A2B3';

// Where an exception or a USB approval request stands: rejected or approved on central, approved
// at the venue (an approver at registration or for USB devices), or still waiting for central.
const DECISIONS = {
    approved: { label: 'Approved', color: '#067647', background: '#ECFDF3', border: '#ABEFC6' },
    rejected: { label: 'Rejected', color: '#B42318', background: '#FEF3F2', border: '#FECDCA' },
    pending: { label: 'Pending approval', color: '#93370D', background: '#FFFAEB', border: '#FEDF89' },
};

const StatusChip = ({ label, color, background, border }) => (
    <Box component='span' sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 10, fontSize: 12, fontWeight: 600, color, bgcolor: background, border: `1px solid ${border}` }}>
        {label}
    </Box>
);

// One approved USB device as a table row (fields as the agent reports them).
const describeUsbDevice = (device) => ({
    device: [device?.vendor, device?.product].filter(Boolean).join(' '),
    id: [device?.vendorId, device?.productId].filter(Boolean).join(':'),
    serial: device?.serial,
    class: device?.class,
    interfaces: Array.isArray(device?.interfaces) ? device.interfaces.join(', ') : '',
    port: device?.port,
});

const entriesOf = (device) => (Array.isArray(device?.approvedDevices) ? device.approvedDevices : []);

// An entry with a `request` block came from a USB approval request: it lets its device in only
// once approved, at the seat it was asked for. One without is an older approval at the kiosk.
const isApprovedHere = (entry, device) => !entry.request || (entry.request.status === 'approved'
    && (entry.request.labNo ?? null) === (device?.labNo ?? null) && (entry.request.seatNo ?? null) === (device?.seatNo ?? null));
const approvedDevicesOf = (device) => entriesOf(device).filter((entry) => isApprovedHere(entry, device));

// The device's USB approval requests, newest first: { ...request block, devices }.
const usbRequestsOf = (device) => {
    const requests = new Map();
    for (const { request, ...entry } of entriesOf(device)) {
        if (!request?.id) continue;
        if (!requests.has(request.id)) requests.set(request.id, { ...request, devices: [] });
        requests.get(request.id).devices.push(entry);
    }
    return [...requests.values()].sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
};

const formatDate = (value) => (value ? dayjs(value).format(DATE_FORMAT) : null);
const decidedAs = (status) => (status === 'rejected' ? 'rejected' : 'approved');
const seatOf = (request) => (request.labNo ? `${request.labNo} / ${request.seatNo}` : 'Not registered yet');

// One USB approval request: its status and details, and the devices it asked for.
const UsbRequest = ({ request }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box><StatusChip {...DECISIONS[request.status] ?? DECISIONS.pending} /></Box>
        <FieldGrid entries={[
            ['labSeat', seatOf(request)],
            ['requestedAt', formatDate(request.requestedAt)],
            ['reason', request.reason],
            ...(request.status === 'pending' ? [] : [
                [`${decidedAs(request.status)}By`, request.decidedByName ?? request.decidedBy],
                [`${decidedAs(request.status)}At`, formatDate(request.decidedAt)],
                ['remark', request.remark],
            ]),
        ]} />
        <ObjectTable items={request.devices.map(describeUsbDevice)} />
    </Box>
);

// Whether the device has an exception: requested from the venue, approved at registration, USB
// devices approved at the kiosk, or a USB approval request.
export const hasDeviceException = (device) => Boolean(device?.isException) || entriesOf(device).length > 0 || Boolean(device?.exceptionReason);

// The device's exception as the Full report's Exception section: { key, name, count, content }.
// It covers every kind: an exception requested from the venue and decided on central, USB devices
// approved at the kiosk, and USB approval requests decided on central. A device with only USB
// approval requests shows the latest one's standing: deviceStatus belongs to the exception flow and
// says nothing about them. previousHardware is { state: 'loading' | 'done' | 'error',
// currentFingerprint, previous, change, basis } from getPreviousHardware.
export const exceptionReportSection = (device, previousHardware) => {
    const approvedDevices = approvedDevicesOf(device);
    const olderApprovals = entriesOf(device).filter((entry) => !entry.request);
    const usbRequests = usbRequestsOf(device);
    const hasException = hasDeviceException(device);
    const latestRequest = !device?.isException && !device?.exceptionReason && !olderApprovals.length ? usbRequests[0] : null;
    const status = latestRequest ? (DECISIONS[latestRequest.status] ? latestRequest.status : 'pending')
        : device?.deviceStatus === 'rejected' ? 'rejected'
        : device?.deviceStatus === 'approved' || device?.exceptionApprovedBy ? 'approved' : 'pending';
    const decidedBy = latestRequest ? latestRequest.decidedByName ?? latestRequest.decidedBy : device?.exceptionApprovedByName ?? device?.exceptionApprovedBy;
    const decidedAt = latestRequest ? latestRequest.decidedAt : device?.exceptionApprovedTimestamp;
    const fields = [
        ['deviceId', device?.deviceId],
        ['macAddress', device?.macAddress],
        [`${decidedAs(status)}By`, status === 'pending' ? null : decidedBy],
        [`${decidedAs(status)}At`, status === 'pending' ? null : formatDate(decidedAt)],
        ['type', [device?.isException && 'Device exception', olderApprovals.length && 'USB devices approved', usbRequests.length && 'USB approval request'].filter(Boolean).join(' · ')],
        ['reason', latestRequest ? latestRequest.reason : device?.exceptionReason],
        ['raisedBy', latestRequest ? null : device?.exceptionCreatedByName ?? device?.exceptionCreatedBy],
    ];

    const content = !hasException
        ? <Typography variant='body2' sx={{ color: MUTED }}>No exception on this device: none was requested and no USB device was approved or requested.</Typography>
        : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box><StatusChip {...DECISIONS[status]} /></Box>
                <FieldGrid entries={fields} />
                <Box>
                    <BlockTitle count={approvedDevices.length}>Approved USB devices</BlockTitle>
                    {approvedDevices.length
                        ? <ObjectTable items={approvedDevices.map(describeUsbDevice)} />
                        : <Typography variant='body2' sx={{ color: MUTED }}>None</Typography>}
                </Box>
                {usbRequests.length > 0 && (
                    <Box>
                        <BlockTitle count={usbRequests.length}>USB approval requests</BlockTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {usbRequests.map((request) => <UsbRequest key={request.id} request={request} />)}
                        </Box>
                    </Box>
                )}
                <HardwareComparison state={previousHardware?.state ?? 'loading'} previous={previousHardware?.previous ?? null} change={previousHardware?.change ?? null} basis={previousHardware?.basis ?? null}
                    current={{ deviceId: device?.deviceId, macAddress: device?.macAddress, fingerprint: previousHardware?.currentFingerprint, hardwareAt: device?.hardwareAt, hardware: device?.hardware }} />
            </Box>
        );

    return { key: '__exception__', name: 'Exception', count: hasException ? (device?.isException ? 1 : 0) + olderApprovals.length + usbRequests.length : 0, content };
};
